import classNames from "classnames";
import { AdobeEventActions } from "../../enum/adobe/adobeEventActions";
import { AdobeEventType } from "../../enum/adobe/adobeEventType";
import { StepNames } from "../../enum/adobe/stepNames";
import { StepNumbers } from "../../enum/adobe/stepNumbers";
import { SubStepNames } from "../../enum/adobe/subStepNames";
import { IconModelEnum } from "../../enum/Button/iconButton.model";
import { VarianButtonEnum } from "../../enum/Button/variantButton.model";
import { ContactDealerFormSettings, ContactDealerSettings, PrivacyPolicySettings } from "../../enum/enums";
import DataContainer from "../../services/DataContainer";
import { Button } from "../Button/Button";
import CaptchaForForms from "../CaptchaForForms/CaptchaForForms";
import { CustomCheckbox } from "../CustomCheckbox/CustomCheckbox";
import { ContactFormThankYouModal } from "../Modal/ContactFormThankYouModal";
import { Typography } from "../typography";
import { ChangeEvent, LegacyRef } from "react";
import { DataType } from "../../pages/Summary/formType";
import { Input } from "../Input/Input";
import { Dropdown } from "../Dropdown/Dropdown";

interface AdobeCampaignFormProps {
  resetRef: LegacyRef<HTMLFormElement> | undefined;
  renderControlKey: number;
  formData: DataType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isEmailValid: boolean;
  onCheckboxChange: (e: ChangeEvent<HTMLInputElement>, name: string) => void;
  marketingCheckboxErrorIndicator: boolean;
  privacyCheckboxErrorIndicator: boolean;
  requiredInput: boolean;
  requiredFields: boolean;
  captchaRequired: boolean;
  resetForm: boolean;
  contactFormModalOpen: boolean;
  setContactFormModalOpen: (value: boolean) => void;
  salesforceStatus: string;
  salesforceMessage: string;
  submitForm: (data: DataType) => void;
  setCaptchaRequired: (value: boolean) => void;
  showCTA: (sectionName: string, key: string) => boolean;
  correctCaptcha: boolean;
  setCorrectCaptcha: (value: boolean) => void;
  chosenDealerRef: LegacyRef<HTMLDivElement> | undefined;
  onChangeDropdown: (e: ChangeEvent<HTMLSelectElement>) => void;
  onChangeRegions: (e: ChangeEvent<HTMLSelectElement>) => void;
  regionSelected: string;
  showSalesForceFields: boolean;
  isSubmitting?: boolean;
}

export const AdobeCampaignForm = ({
  resetRef,
  renderControlKey,
  formData,
  onChange,
  isEmailValid,
  onCheckboxChange,
  marketingCheckboxErrorIndicator,
  privacyCheckboxErrorIndicator,
  requiredInput,
  requiredFields,
  captchaRequired,
  resetForm,
  contactFormModalOpen,
  setContactFormModalOpen,
  salesforceStatus,
  salesforceMessage,
  submitForm,
  setCaptchaRequired,
  showCTA,
  correctCaptcha,
  setCorrectCaptcha,
  chosenDealerRef,
  onChangeDropdown,
  onChangeRegions,
  regionSelected,
  showSalesForceFields,
  isSubmitting = false,
}: AdobeCampaignFormProps) => {
  return (
    <form className="pb-7" ref={resetRef} key={renderControlKey}>
      <Input
        variant="primary"
        type="text"
        name="name"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_FIRSTNAME_TEXT
        )}
        value={formData.name}
        onChangeEvent={onChange}
        onClickEvent={() => DataContainer.sendDataToAdobeDataLayer(AdobeEventType.APPLICATION_INTERACTION, StepNumbers.STEP_8, StepNames.SUMMARY, SubStepNames.SUMMARY, AdobeEventActions.FORM_START, {}, "")}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_NAME_REQUIRED) === "true" && requiredInput}
      />
      {showSalesForceFields &&
        <Input
          variant="primary"
          type="text"
          name="lastname"
          label={DataContainer.sectionKey(
            ContactDealerFormSettings.CONTACT_DEALER_FORM,
            ContactDealerFormSettings.CONTACT_DEALER_LASTNAME_TEXT
          )}
          value={formData.lastname}
          onChangeEvent={onChange}
          isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_LASTNAME_REQUIRED) === "true" && requiredInput}
        />
      }

      <Input
        variant="primary"
        type="email"
        name="email"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_EMAIL_TEXT
        )}
        value={formData.email}
        onChangeEvent={onChange}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_REQUIRED) === "true" && requiredInput}
      />
      {(!isEmailValid && formData.email !== "") &&
                    <span className="text-red text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_INVALID_TEXT)}</span>
      }
      <Input
        variant="primary"
        type="tel"
        name="phone"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_PHONE_TEXT
        )}
        onKeyDownEvent={(evt) => ["e", "E"].includes(evt.key) && evt.preventDefault()}
        value={formData.phone === "default" ? DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_PHONE_COUNTRY_CODE) : formData.phone}
        onChangeEvent={onChange}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_PHONE_REQUIRED) === "true" && requiredInput}
      />
      <Input
        variant="primary"
        type="text"
        name="zip"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_ZIP_TEXT
        )}
        value={formData.zip}
        onChangeEvent={onChange}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_ZIP_REQUIRED) === "true" && requiredInput}
      />
      {!showSalesForceFields &&
        <Input
          variant="primary"
          type="text"
          name="country"
          label={DataContainer.sectionKey(
            ContactDealerFormSettings.CONTACT_DEALER_FORM,
            ContactDealerFormSettings.CONTACT_DEALER_COUNTRY_TEXT
          )}
          value={formData.country}
          onChangeEvent={onChange}
          isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_COUNTRY_REQUIRED) === "true" && requiredInput}
        />
      }
      <Input
        variant="primary"
        type="text"
        name="city"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_CITY_TEXT
        )}
        value={formData.city}
        onChangeEvent={onChange}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_CITY_REQUIRED) === "true" && requiredInput}
      />
      <Input
        variant="primary"
        type="text"
        name="company"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_COMPANY_TEXT
        )}
        value={formData.company}
        onChangeEvent={onChange}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_COMPANY_REQUIRED) === "true" && requiredInput}
      />
      {!showSalesForceFields &&
        <Dropdown
          variant="primary"
          label={DataContainer.sectionKey(
            ContactDealerFormSettings.CONTACT_DEALER_FORM,
            ContactDealerFormSettings.CONTACT_DEALER_SEGMENT_TEXT
          )}
          name="segment"
          selectedOption={formData?.segment}
          onChangeEvent={onChangeDropdown}
          options={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_SEGMENT_LIST).split(",").map((option) => ({ id: option, option }))}
          isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_SEGMENT_REQUIRED) === "true" && requiredInput}
        />
      }
      {DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.REGIONS_IN_FORM) !== "false" &&
        <Dropdown
          name="regions"
          variant={"primary"}
          label={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_REGIONS_TEXT)}
          options={DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.REGIONS_LIST).split(", ").map((option) => ({ id: option, option }))}
          onChangeEvent={onChangeRegions}
          selectedOption={regionSelected}
          isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_REGIONS_REQUIRED) === "true" && requiredInput}
        />
      }
      <Input
        variant="primary"
        type="textarea"
        name="message"
        label={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_MESSAGE_TEXT
        )}
        value={formData.message}
        onChangeEvent={onChange}
        isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_MESSAGE_REQUIRED) === "true" && requiredInput}
      />
      <Typography tag="p" className="text-2xs">
        {DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_REQUIRED_TIP_TEXT
        )}
      </Typography>
      <div>
        {showCTA(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SHOW_CHOOSE_DEALER) &&
        <div ref={chosenDealerRef} className="flex justify-between">
          <Typography tag="p" className="text-3xl py-7 pr-2 text-black-medium">
            {DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_CHOSEN_DEALER_TEXT
            )}
            {(formData?.chosen_dealer?.name === "" && requiredInput) && <span className="text-red flex text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_CHOSEN_DEALER_REQUIRED_TEXT )}</span>}
          </Typography>
          <Typography tag="p" className="text-3xl py-7 text-black-medium">
            {formData?.chosen_dealer?.name}
          </Typography>
        </div>
        }
      </div>

      <div className="my-5">
        <Typography tag="p" className="text-3xl text-black-medium">
          {DataContainer.sectionKey(
            PrivacyPolicySettings.PRIVACY_POLICY,
            PrivacyPolicySettings.PRIVACY_POLICY_TITLE
          )}
        </Typography>
        <Typography tag="p" className="text-2xl text-light-grey pb-7">
          {DataContainer.sectionKey(
            PrivacyPolicySettings.PRIVACY_POLICY,
            PrivacyPolicySettings.PRIVACY_POLICY_TEXT
          )}
        </Typography>
      </div>
      <div className="flex flex-col py-6">
        <div className='relative'>
          <CustomCheckbox
            name="pronounTitle"
            label={DataContainer.sectionKey(PrivacyPolicySettings.PRIVACY_POLICY, PrivacyPolicySettings.PRIVACY_POLICY_CHECKBOX_TEXT)}
            onChangeEvent={(e) => onCheckboxChange(e, "pronounTitle")}
            customClassNames={classNames({ "border-red rounded": privacyCheckboxErrorIndicator && requiredInput })}
            customLabelClasses={classNames({ "text-red": privacyCheckboxErrorIndicator && requiredInput })}
          />
        </div>
        {(privacyCheckboxErrorIndicator && requiredInput) && <span className="text-red pt-2 text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_ACCEPT_TERMS_REQUIRED_TEXT)}</span>}
      </div>
      {showCTA(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX)&&
        <div className="flex flex-col pb-6">
          <div className='relative'>
            <CustomCheckbox
              name="marketingCheckbox"
              label={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_TEXT)}
              onChangeEvent={(e) => onCheckboxChange(e, "marketingCheckbox")}
              customClassNames={classNames({ "border-red rounded": marketingCheckboxErrorIndicator && requiredInput && (DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true") })}
              customLabelClasses={classNames({ "text-red": marketingCheckboxErrorIndicator && requiredInput && (DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true")})}
            />
          </div>
          {(marketingCheckboxErrorIndicator && requiredInput && showCTA(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED)) && <span className="text-red pt-2 text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED_TEXT)}</span>}
        </div>
      }
      <CaptchaForForms salesforce={false} required={!(captchaRequired || requiredFields)} captcha={(e) => setCaptchaRequired(e)} resetCaptcha={resetForm} setCorrectCaptcha={(e) => setCorrectCaptcha(e)} correctCaptcha={correctCaptcha}/>
      <Button
        variant={VarianButtonEnum.PRIMARY}
        text={DataContainer.sectionKey(
          ContactDealerFormSettings.CONTACT_DEALER_FORM,
          ContactDealerFormSettings.CONTACT_DEALER_SEND_TEXT
        )}
        icon={isSubmitting ? IconModelEnum.LOADING : IconModelEnum.SUBMIT}
        customClassNames="bg-blue-light"
        disabled={isSubmitting}
        onClickEvent={() => {
          submitForm(formData);
        }}
      />
      {(contactFormModalOpen) ? (
        <ContactFormThankYouModal
          setContactFormModalOpen={setContactFormModalOpen}
          salesforceStatus={salesforceStatus}
          salesforceMessage={salesforceMessage}
        />

      ) : null}
    </form>
  );
};